/*
 * Storm Creek - Accessibility fixes
 *
 * Handles keyboard navigation, focus management, focus traps,
 * live-region announcements and pause controls that cannot be
 * expressed in Liquid templates alone.
 */
(function () {
  'use strict';

  var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function matches(el, selector) {
    return el && el.matches && el.matches(selector);
  }

  function isFocusable(el) {
    return el && !el.hasAttribute('disabled') && !el.hasAttribute('hidden') && el.offsetParent !== null;
  }

  function trapFocus(container, focusContainer) {
    if (!container) { return; }
    var previouslyFocused = document.activeElement;
    var focusables = function () {
      return Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(isFocusable);
    };

    if (focusables().length) {
      var first = focusables()[0];
      var last = focusables()[focusables().length - 1];
      if (focusContainer) {
        container.focus({ preventScroll: true });
      } else {
        (document.activeElement && container.contains(document.activeElement) ? document.activeElement : first).focus();
      }
      container.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') { return; }
        var list = focusables();
        if (!list.length) {
          e.preventDefault();
          container.focus();
          return;
        }
        first = list[0];
        last = list[list.length - 1];
        if (document.activeElement === container) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    }
    return previouslyFocused;
  }

  /* ------------------------------------------------------------------ *
   * 1. Header navigation: keyboard support for top-level dropdowns      *
   * ------------------------------------------------------------------ */

  function initHeaderNav() {
    /* 1. Block the theme's own focus handler (it re-opens the menu on
       focus, including when focus returns after Escape). We manage
       open-on-focus ourselves below so Escape can close without an
       immediate reopen. */
    function blockFocusOpen(e) {
      var link = e.target.closest && e.target.closest('.header__desktop .menu__item [data-top-link]');
      if (link) {
        e.stopImmediatePropagation();
      }
    }

    document.addEventListener('focus', blockFocusOpen, true);

    var drawers = document.querySelectorAll('.header__desktop .menu__item[data-hover-disclosure-toggle]');
    var drawerItems = Array.prototype.slice.call(drawers);

    /* 2. Issue: When focus moves to another top-level item, close
       any previously opened desktop mega menu. */
    function closeOtherDrawers(activeItem) {
      drawerItems.forEach(function (drawerItem) {
        if (drawerItem !== activeItem && drawerItem.classList.contains('is-visible')) {
          var drawerLink = drawerItem.querySelector('[data-top-link]');
          var drawerDisclosure = drawerItem.querySelector('[data-hover-disclosure]');
          drawerItem.classList.remove('is-visible');
          if (drawerDisclosure) {
            drawerDisclosure.classList.remove('is-visible');
            drawerDisclosure.setAttribute('aria-expanded', 'false');
            drawerDisclosure.setAttribute('aria-hidden', 'true');
          }
          if (drawerLink) {
            drawerLink.setAttribute('aria-expanded', 'false');
          }
        }
      });
    }

    document.addEventListener('focusin', function (e) {
      var activeItem = e.target.closest && e.target.closest('.header__desktop .menu__item[data-hover-disclosure-toggle]');
      closeOtherDrawers(activeItem || null);
    }, true);

    function getFocusable(container) {
      return Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(isFocusable);
    }

    function restoreDropdownLinks(disclosure) {
      if (!disclosure) { return; }
      disclosure.querySelectorAll('[data-a11y-restored-tabindex]').forEach(function (el) {
        el.removeAttribute('tabindex');
        el.removeAttribute('data-a11y-restored-tabindex');
      });
    }

    function bindItem(inputItem) {
      var item = inputItem;
      if (item.getAttribute('data-a11y-nav-bound')) { return; }
      item.setAttribute('data-a11y-nav-bound', 'true');

      var link = item.querySelector('[data-top-link]');
      var wrapper = item.closest('[data-header-wrapper]');
      var disclosure = item.querySelector('[data-hover-disclosure]');
      var isGrandparent = item.classList.contains('grandparent');
      var suppressFocusOpen = false;
      if (!link || !disclosure) { return; }

      var wrapperAriaAttrs = ['aria-controls', 'aria-expanded', 'aria-haspopup'];

      function clearWrapperAria() {
        wrapperAriaAttrs.forEach(function (attr) {
          if (item.hasAttribute(attr)) {
            item.removeAttribute(attr);
          }
        });
      }

      function open() {
        closeOtherDrawers(item);
        item.classList.add('is-visible');
        disclosure.classList.add('is-visible');
        disclosure.setAttribute('aria-expanded', 'true');
        disclosure.setAttribute('aria-hidden', 'false');
        restoreDropdownLinks(disclosure);
        link.setAttribute('aria-expanded', 'true');
        if (isGrandparent && wrapper) { wrapper.classList.add('meganav--visible'); }
      }

      function close() {
        item.classList.remove('is-visible');
        disclosure.classList.remove('is-visible');
        disclosure.setAttribute('aria-expanded', 'false');
        disclosure.setAttribute('aria-hidden', 'true');
        link.setAttribute('aria-expanded', 'false');
        if (isGrandparent && wrapper) { wrapper.classList.remove('meganav--visible'); }
      }

      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');
      disclosure.setAttribute('aria-expanded', 'false');
      disclosure.setAttribute('aria-hidden', 'true');
      clearWrapperAria();

      item.addEventListener('mouseenter', function () { restoreDropdownLinks(disclosure); });
      item.addEventListener('focusin', function () { restoreDropdownLinks(disclosure); });

      var wrapperObserver = new MutationObserver(clearWrapperAria);
      wrapperObserver.observe(item, { attributes: true, attributeFilter: wrapperAriaAttrs });

      link.addEventListener('focusin', function () {
        if (!suppressFocusOpen) {
          open();
        }
      });

      link.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          suppressFocusOpen = true;
          close();
          link.focus();
          window.setTimeout(function () { suppressFocusOpen = false; }, 0);
        } else if (e.key === 'Enter') {
          if (!item.classList.contains('is-visible')) {
            e.preventDefault();
            open();
          }
        } else if (e.key === ' ') {
          e.preventDefault();
          if (item.classList.contains('is-visible')) {
            close();
          } else {
            open();
          }
        }
      });

      disclosure.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          e.stopPropagation();
          suppressFocusOpen = true;
          close();
          link.focus();
          window.setTimeout(function () { suppressFocusOpen = false; }, 0);
        } else if (e.key === 'Tab' && !e.shiftKey) {
          var focusables = getFocusable(disclosure);
          if (focusables.length && document.activeElement === focusables[focusables.length - 1]) {
            close();
          }
        }
      });

      /* 3. Issue: Collapse the menu when focus leaves the open item. */
      item.addEventListener('focusout', function (e) {
        var next = e.relatedTarget;
        if (next && item.contains(next)) { return; }
        if (!next || !item.contains(next)) { close(); }
      });
    }

    drawerItems.forEach(bindItem);

    /* Re-bind menu items if the header section is re-rendered (for
       example, when Shopify swaps in a fresh header). Bound items are
       marked with data-a11y-nav-bound so they are never bound twice. */
    function bindAddedItems() {
      document.querySelectorAll('.header__desktop .menu__item[data-hover-disclosure-toggle]:not([data-a11y-nav-bound])').forEach(bindItem);
    }
    var navRebindTimer = null;
    var navObserver = new MutationObserver(function () {
      clearTimeout(navRebindTimer);
      navRebindTimer = setTimeout(bindAddedItems, 100);
    });
    navObserver.observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ *
   * 2. Community / About dropdown toggles (non-links)                   *
   * ------------------------------------------------------------------ */

  function initDropdownToggles() {
    /* 4. Issue: Community/About buttons must toggle on activation,
       announce expanded state, and close on Escape or tab-out. */
    document.querySelectorAll('.dropdownn > .dropdown-toggle').forEach(function (toggle) {
      var wrap = toggle.closest('.dropdownn');
      if (!wrap) { return; }
      var content = wrap.querySelector('.dropdownn-content');
      if (!content) { return; }
      var hoverCloseTimer = null;
      var suppressFocusOpen = false;

      toggle.setAttribute('aria-haspopup', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      content.setAttribute('tabindex', '-1');
      if (!toggle.hasAttribute('aria-controls') && content.id) {
        toggle.setAttribute('aria-controls', content.id);
      }

      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        toggleDropdown();
      });

      toggle.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          suppressFocusOpen = true;
          closeDropdown();
          toggle.focus();
          window.setTimeout(function () { suppressFocusOpen = false; }, 0);
        }
      });

      function toggleDropdown() {
        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        var nextExpanded = !expanded;
        toggle.setAttribute('aria-expanded', String(nextExpanded));
        content.setAttribute('aria-expanded', String(nextExpanded));
      }

      function openDropdown() {
        if (suppressFocusOpen) { return; }
        if (hoverCloseTimer) {
          window.clearTimeout(hoverCloseTimer);
          hoverCloseTimer = null;
        }
        toggle.setAttribute('aria-expanded', 'true');
        content.setAttribute('aria-expanded', 'true');
        positionFixedContent();
      }

      function closeDropdown() {
        toggle.setAttribute('aria-expanded', 'false');
        content.setAttribute('aria-expanded', 'false');
      }

      /* 1. Issue: Keep the fixed-position About bar glued to its trigger
         while the page scrolls so no gap forms between them. */
      function positionFixedContent() {
        if (window.getComputedStyle(content).position !== 'fixed') { return; }
        var rect = wrap.getBoundingClientRect();
        content.style.top = (rect.bottom + 1) + 'px';
      }

      window.addEventListener('scroll', function () {
        if (toggle.getAttribute('aria-expanded') === 'true') {
          positionFixedContent();
        }
      }, { passive: true, capture: true });

      function closeDropdownSoon() {
        if (hoverCloseTimer) {
          window.clearTimeout(hoverCloseTimer);
        }
        hoverCloseTimer = window.setTimeout(function () {
          if (!wrap.matches(':hover') && !content.matches(':hover') && !wrap.contains(document.activeElement)) {
            closeDropdown();
          }
        }, 250);
      }

      /* 2. Issue: About/Community submenus open on MOUSE focus (hover) but
         not on TAB focus. Tabbing to the trigger keeps the menu closed;
         hover or activation (click / Enter / Space) opens it. */
      wrap.addEventListener('mouseenter', openDropdown);
      content.addEventListener('mouseenter', openDropdown);
      wrap.addEventListener('mouseleave', closeDropdownSoon);
      content.addEventListener('mouseleave', closeDropdownSoon);

      /* 4a. Issue: Keep Community/About dropdowns open when keyboard focus
         moves from the trigger into submenu links. */
      content.addEventListener('focusin', openDropdown);

      content.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          suppressFocusOpen = true;
          closeDropdown();
          toggle.focus();
          window.setTimeout(function () { suppressFocusOpen = false; }, 0);
        } else if (e.key === 'Tab' && !e.shiftKey) {
          var focusables = Array.prototype.slice.call(content.querySelectorAll(FOCUSABLE)).filter(isFocusable);
          if (focusables.length && document.activeElement === focusables[focusables.length - 1]) {
            closeDropdown();
          }
        }
      });

      wrap.addEventListener('focusout', function () {
        window.setTimeout(function () {
          if (!wrap.contains(document.activeElement)) {
            closeDropdown();
          }
        }, 0);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Cart drawer: dialog semantics, focus trap + return, Esc          *
   * ------------------------------------------------------------------ */

  function initCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) { return; }

    var returnFocusTo = null;
    var active = false;

    function rememberCartTrigger(trigger) {
      if (trigger && trigger.matches && trigger.matches('[data-cart-toggle]')) {
        returnFocusTo = trigger;
      }
    }

    document.addEventListener('click', function (e) {
      rememberCartTrigger(e.target.closest && e.target.closest('[data-cart-toggle]'));
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') { return; }
      rememberCartTrigger(e.target.closest && e.target.closest('[data-cart-toggle]'));
    }, true);

    function open() {
      var trigger = document.activeElement;
      if (!returnFocusTo && trigger && !drawer.contains(trigger)) {
        returnFocusTo = trigger;
      }
      active = true;
      drawer.setAttribute('aria-hidden', 'false');
      // Focus only the dialog's close control on open so the screen reader
      // announces the dialog + close button, not the entire drawer contents.
      trapFocus(drawer, false);
    }

    function close() {
      active = false;
      drawer.setAttribute('aria-hidden', 'true');
      if (returnFocusTo) {
        try { returnFocusTo.focus(); } catch (err) {}
        returnFocusTo = null;
      }
    }

    function getDrawerFocusables() {
      return Array.prototype.slice.call(drawer.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return isFocusable(el) && el.getAttribute('aria-hidden') !== 'true' && el.getAttribute('tabindex') !== '-1';
      });
    }

    function keepFocusInDrawer(e) {
      if (!active || e.key !== 'Tab') { return; }
      var focusables = getDrawerFocusables();
      if (!focusables.length) {
        e.preventDefault();
        try { drawer.focus({ preventScroll: true }); } catch (err) {}
        return;
      }

      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var current = document.activeElement;

      if (!drawer.contains(current)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }

      if (e.shiftKey && current === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && active) {
        close();
      }
    });

    document.addEventListener('keydown', keepFocusInDrawer, true);

    document.addEventListener('focusin', function (e) {
      if (!active || drawer.contains(e.target)) { return; }
      var focusables = getDrawerFocusables();
      var first = focusables[0] || drawer;
      window.setTimeout(function () {
        if (active && !drawer.contains(document.activeElement)) {
          try { first.focus({ preventScroll: true }); } catch (err) {}
        }
      }, 0);
    }, true);

    /* Observe open/close state via the class the theme adds */
    var observer = new MutationObserver(function () {
      var isOpen = drawer.classList.contains('is-open') || drawer.getAttribute('aria-hidden') !== 'true';
      if (isOpen && !active) {
        open();
      } else if (!isOpen && active) {
        close();
      }
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });

    var closeButton = drawer.querySelector('[data-cart-drawer-close]');
    if (closeButton) {
      closeButton.addEventListener('click', close);
    }
  }

  /* ------------------------------------------------------------------ *
   * 4. Mobile menu drawer: return focus to hamburger & trap focus       *
   * ------------------------------------------------------------------ */

  function initMobileMenu() {
    var drawer = document.querySelector('[data-drawer="hamburger"]');
    if (!drawer) { return; }
    var toggle = document.querySelector('[data-drawer-toggle="hamburger"]');
    var previouslyFocused = null;

    function onOpen() {
      previouslyFocused = document.activeElement;
    }
    function onClose() {
      if (previouslyFocused && toggle) { previouslyFocused = toggle; }
      if (previouslyFocused) {
        try { previouslyFocused.focus(); } catch (err) {}
      }
      previouslyFocused = null;
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        var closeBtn = drawer.querySelector('[data-drawer-toggle="hamburger"]');
        if (closeBtn) { closeBtn.click(); }
      }
    });
    var mo = new MutationObserver(function () {
      if (drawer.classList.contains('is-open')) { onOpen(); } else { onClose(); }
    });
    mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ------------------------------------------------------------------ *
   * 5. Quick-add <dialog> focus management                              *
   * ------------------------------------------------------------------ */

  function initQuickAddDialogs() {
    function scheduleQuickAddCleanup(dialog) {
      var attempts = 0;
      var timer = window.setInterval(function () {
        attempts++;
        hideQuickAddSlides(dialog);
        cleanupQuickAddSwatches(dialog);
        if (attempts >= 12 || !document.body.contains(dialog)) {
          window.clearInterval(timer);
        }
      }, 200);
    }

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add-btn]');
      if (!btn) { return; }
      setTimeout(function () {
        var dialog = document.querySelector('dialog.product-quick-add[open]');
        if (!dialog) { return; }
        dialog.removeAttribute('inert');
        // Native showModal() already traps focus; re-focus safe close target.
        hideQuickAddSlides(dialog);
        cleanupQuickAddSwatches(dialog);
        scheduleQuickAddCleanup(dialog);
        var first = dialog.querySelector('button.product-quick-add__close, button');
        if (first) { first.focus(); }
      }, 250);
    });

    /* Keep focus inside the dialog if the theme opens it without showModal() */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') { return; }
      var dialog = document.querySelector('dialog.product-quick-add[open]');
      if (!dialog) { return; }
      var focusables = Array.prototype.slice.call(dialog.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return isFocusable(el) && el.getAttribute('aria-hidden') !== 'true' && el.getAttribute('tabindex') !== '-1';
      });
      if (!focusables.length) { return; }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ------------------------------------------------------------------ *
   * 6. Live announcements for cart quantity / removal / totals          *
   * ------------------------------------------------------------------ */

  function initCartAnnouncements() {
    var statusHosts = Array.prototype.slice.call(document.querySelectorAll('[data-cart-status]'));
    if (!statusHosts.length) { return; }
    var pendingAddToCart = false;
    var pendingAddToCartToken = 0;
    var pendingQuantityAnnouncement = null;
    var suppressSummaryUntil = 0;

    function announce(text) {
      if (!text) { return; }
      // Announce on a single status host to avoid duplicate read-outs.
      // Only the first matching host is used; focus is left on the quantity
      // control so the live region is not read twice (on focus + on change).
      var statusHost = statusHosts[0];
      if (!statusHost) { return; }
      statusHost.textContent = '';
      // force reflow so repeated identical messages are re-announced
      void statusHost.offsetWidth;
      statusHost.textContent = text;
    }

    function getCartSummary() {
      var parts = [];
      var total = document.querySelector('[data-cart-price-holder] [data-cart-total]');
      var freeShipping = document.querySelector('[data-free-shipping]');
      if (total && total.textContent.trim()) {
        parts.push('Cart total: ' + total.textContent.trim());
      }
      if (freeShipping && freeShipping.textContent.trim()) {
        parts.push(freeShipping.textContent.trim().replace(/\s+/g, ' '));
      }
      return parts.join('. ');
    }

    function restoreQuantityFocus(id, action, expectedValue) {
      if (!id || !action) { return; }

      // Fallback completion hook: the theme dispatches "theme:cart:change"
      // in build() after every successful cart AJAX update, so focus the
      // freshly rebuilt control the moment the rebuild completes.
      var finished = false;
      var finish = function () {
        if (finished) { return; }
        finished = true;
        document.removeEventListener('theme:cart:change', finish);
        var line = document.querySelector('.cart__item[data-item="' + id + '"]');
        var button = line && line.querySelector('[data-quantity-' + action + ']');
        if (button && !button.disabled && !button.hasAttribute('disabled')) {
          try { button.focus({ preventScroll: true }); } catch (err) {}
        }
      };
      document.addEventListener('theme:cart:change', finish);
      window.setTimeout(function () {
        document.removeEventListener('theme:cart:change', finish);
      }, 8000);

      var started = Date.now();
      var attempts = 0;
      var timer = window.setInterval(function () {
        var line = document.querySelector('.cart__item[data-item="' + id + '"]');
        var field = line && line.querySelector('.cart__quantity-field');
        var value = field ? parseInt(field.value, 10) : 0;
        var done = false;
        if (expectedValue !== null) {
          if (expectedValue <= 0) {
            done = !line || (field && value <= expectedValue);
          } else {
            done = !!field && value === expectedValue;
          }
        }
        // Only focus once the rebuilt button exists and is not disabled.
        // The theme disables every cart button while the AJAX update runs,
        // so focusing before enablement would fail and focus stays on body.
        var button = line && line.querySelector('[data-quantity-' + action + ']');
        var ready = done && button && !button.hasAttribute('disabled') && !button.disabled;

        if (ready || Date.now() - started > 5000) {
          if (ready && button && attempts < 4) {
            attempts++;
            try { button.focus({ preventScroll: true }); } catch (err) {}
            if (document.activeElement === button) {
              window.clearInterval(timer);
              return;
            }
            // Focus did not land (element re-rendered mid-focus); retry.
            return;
          }
          window.clearInterval(timer);
        }
      }, 120);
    }

    function queueAddToCartAnnouncement() {
      pendingAddToCart = true;
      pendingAddToCartToken += 1;
      var token = pendingAddToCartToken;
      window.setTimeout(function () {
        if (token === pendingAddToCartToken) { pendingAddToCart = false; }
      }, 8000);
    }

    document.addEventListener('theme:cart:change', function () {
      if (!pendingAddToCart) { return; }
      pendingAddToCart = false;
      pendingAddToCartToken += 1;
      window.setTimeout(function () {
        announce('Item added to cart.');
      }, 100);
    });

    document.addEventListener('theme:cart:change', function () {
      if (!pendingQuantityAnnouncement) { return; }
      var pending = pendingQuantityAnnouncement;
      pendingQuantityAnnouncement = null;
      suppressSummaryUntil = Date.now() + 1200;
      window.setTimeout(function () {
        var currentLine = pending.id && document.querySelector('[data-item="' + pending.id + '"]');
        var field = currentLine && currentLine.querySelector('.cart__quantity-field');
        var price = currentLine && currentLine.querySelector('[data-item-price]');
        if (!field) { return; }
        var message = (pending.title ? pending.title + ', ' : '') + 'quantity updated to ' + field.value + (price ? ', item total ' + price.textContent.trim() : '');
        var summary = getCartSummary();
        announce(message + (summary ? '. ' + summary : ''));
      }, 100);
    });

    document.addEventListener('submit', function (e) {
      if (e.target.closest && e.target.closest('.product-quick-add')) {
        queueAddToCartAnnouncement();
      }
    }, true);

    document.addEventListener('click', function (e) {
      var minus = e.target.closest('[data-quantity-minus]');
      var plus = e.target.closest('[data-quantity-plus]');
      var remove = e.target.closest('[data-item-remove]');
      var addToCart = e.target.closest('.product-quick-add [data-add-to-cart]');
      if (addToCart) {
        queueAddToCartAnnouncement();
      }
      if (minus || plus) {
        var button = minus || plus;
        var line = button.closest('.cart__item');
        var id = button.getAttribute('data-id');
        var action = plus ? 'plus' : 'minus';
        var title = line && line.getAttribute('data-item-title');
        var field = line && line.querySelector('.cart__quantity-field');
        var currentValue = field ? parseInt(field.value, 10) : 0;
        var expectedValue = plus ? currentValue + 1 : currentValue - 1;
        announce('Updating quantity.');
        pendingQuantityAnnouncement = { id: id, title: title };
        restoreQuantityFocus(id, action, expectedValue);
      }
      if (remove) {
        var titleR = remove.closest('.cart__item');
        var name = titleR && titleR.getAttribute('data-item-title');
        setTimeout(function () {
          announce('Removed from cart' + (name ? ': ' + name : ''));
        }, 350);
      }
    });

    /* Announce subtotal updates */
    var priceNode = document.querySelector('[data-cart-price-holder]');
    if (priceNode) {
      var mo = new MutationObserver(function () {
        if (Date.now() < suppressSummaryUntil) { return; }
        var summary = getCartSummary();
        if (summary) { announce(summary); }
      });
      mo.observe(priceNode, { childList: true, subtree: true, characterData: true });
    }
  }

  /* ------------------------------------------------------------------ *
   * 7. (removed) Pause/Play controls for auto-moving content            *
   * ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------ *
   * 8. Search: move focus / announce results                            *
   * ------------------------------------------------------------------ */

  function initSearchFocus() {
    // Search uses a full page navigation, so there are two moments to cover:
    //
    // 1. On the ORIGINAL page, when the search form is submitted, announce
    //    "Loading search results" so the user gets immediate feedback that
    //    the search is in progress (the page is about to navigate away).
    //
    // 2. On the RESULTS page load, announce the pre-rendered result count.
    //    We do this two ways for reliability across screen readers:
    //       a) populate a role="status" live region AFTER a delay so JAWS
    //          announces it as a status message, and
    //       b) move focus to the caption with tabindex="-1" as a fallback.
    var form = document.querySelector('main-search .search-form');
    if (form) {
      form.addEventListener('submit', function () {
        if (window.__a11ySearchStatus) {
          window.__a11ySearchStatus.textContent = '';
          // force reflow so the same message re-announces if repeated
          void window.__a11ySearchStatus.offsetWidth;
          window.__a11ySearchStatus.textContent = 'Loading search results';
        }
      });
    }

    var caption = document.querySelector('.search__caption');
    if (!caption) { return; }
    var countText = caption.textContent.trim();

    // (a) Populate the live region after a delay so it is announced as a
    //     status message independent of focus/page-read timing.
    if (window.__a11ySearchStatus) {
      window.setTimeout(function () {
        window.__a11ySearchStatus.textContent = '';
        void window.__a11ySearchStatus.offsetWidth;
        window.__a11ySearchStatus.textContent = countText;
      }, 500);
    }

    // (b) Move focus to the search input so the screen reader starts at a
    //     known, small element instead of re-reading the whole results page.
    window.setTimeout(function () {
      var input = document.querySelector('main-search .search-box');
      if (input) {
        try { input.focus({ preventScroll: true }); } catch (err) {}
      }
    }, 150);
  }

  /* ------------------------------------------------------------------ *
   * 9. Skip duplicate announcements on product media / rating graphics  *
   * ------------------------------------------------------------------ */

  function initAriaCleanup() {
    document.querySelectorAll('.grid__rating__stars, .star-rating--stars, [data-star-rating] svg').forEach(function (el) {
      el.setAttribute('aria-hidden', 'true');
    });
    /* Decorative swatch graphics inside quick-add modal */
    document.querySelectorAll('.product-quick-add [data-color-swatch] img').forEach(function (el) {
      el.setAttribute('aria-hidden', 'true');
    });
    hideQuickAddSlides(document);
    cleanupQuickAddSwatches(document);
    cleanupGridSwatchLinks(document);
  }

  function cleanupGridSwatchLinks(root) {
    if (!root || !root.querySelectorAll) { return; }
    root.querySelectorAll('[data-swatch-link]').forEach(function (link) {
      var tooltipParent = link.closest('[data-tooltip]');
      var value = link.getAttribute('data-swatch') || (tooltipParent && tooltipParent.getAttribute('data-tooltip')) || link.textContent || '';
      value = value.replace(/^\s+|\s+$/g, '');
      if (value) {
        link.setAttribute('aria-label', 'Color: ' + value);
        link.setAttribute('title', value);
      }
      link.querySelectorAll('img, svg, [role="img"]').forEach(function (el) {
        if (el.tagName && el.tagName.toLowerCase() === 'img') { el.setAttribute('alt', ''); }
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('role', 'presentation');
      });
    });
  }

  function initGridSwatchObserver() {
    cleanupGridSwatchLinks(document);
    initColorCountFocusReveal(document);
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) {
            cleanupGridSwatchLinks(node);
            initColorCountFocusReveal(node);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function setColorSwatchesVisible(holder, visible) {
    if (!holder) { return; }
    var card = holder.closest('.product-item');
    if (card) { card.classList.toggle('a11y-swatches-visible', visible); }
    var nodes = [holder].concat(Array.prototype.slice.call(holder.querySelectorAll('.radio__fieldset--swatches, [data-grid-swatch-form], [data-grid-swatches], .selector-wrapper__scrollbar, .swatch__button, [data-swatch-button], .swatch__link, .sibling__link')));
    nodes.forEach(function (el) {
      if (visible) {
        el.classList.add('is-visible');
        el.style.setProperty('visibility', 'visible', 'important');
        el.style.setProperty('opacity', '1', 'important');
      } else {
        el.classList.remove('is-visible');
        el.style.removeProperty('visibility');
        el.style.removeProperty('opacity');
      }
    });
  }

  function initColorCountFocusReveal(root) {
    if (!root || !root.querySelectorAll) { return; }
    root.querySelectorAll('.product-item__swatches__count').forEach(function (count) {
      if (count._a11yFocusRevealBound) { return; }
      count._a11yFocusRevealBound = true;
      var holder = count.closest('.product-item__swatches__holder');
      if (!holder) { return; }
      var card = holder && holder.closest('.product-item');
      var productLink = card && card.querySelector('.product-link:not([aria-hidden="true"])');

      function getSwatches() {
        if (!holder) { return []; }
        return Array.prototype.slice.call(holder.querySelectorAll('[data-swatch-link], .sibling__link'));
      }

      function openSwatches(focusFirst) {
        setColorSwatchesVisible(holder, true);
        if (focusFirst) {
          var first = getSwatches()[0];
          if (first) { window.setTimeout(function () { first.focus(); }, 0); }
        }
      }

      if (productLink) {
        productLink.addEventListener('focus', function () {
          openSwatches(false);
        });
      }

      holder.addEventListener('focusin', function () {
        openSwatches(false);
      });

      if (holder) {
        holder.addEventListener('keydown', function (event) {
          if (event.key !== 'Tab') { return; }
          var swatches = getSwatches();
          var index = swatches.indexOf(document.activeElement);
          if (index === -1) { return; }
          if (event.shiftKey && index === 0) {
            event.preventDefault();
            if (productLink) { productLink.focus(); }
          } else if (!event.shiftKey && index < swatches.length - 1) {
            event.preventDefault();
            swatches[index + 1].focus();
          }
        });
      }

      if (holder) {
        holder.addEventListener('focusout', function () {
          window.setTimeout(function () {
            if (!holder.contains(document.activeElement)) {
              setColorSwatchesVisible(holder, false);
            }
          }, 0);
        });
      }
    });
  }

  function cleanupQuickAddSwatches(root) {
    if (!root || !root.querySelectorAll) { return; }
    var swatches = root.querySelectorAll('.product-quick-add .swatch__button');
    for (var i = 0; i < swatches.length; i++) {
      var swatch = swatches[i];
      var input = swatch.querySelector('input[type="radio"]');
      var label = swatch.querySelector('label[data-swatch]');
      if (!input || !label) { continue; }

      var value = input.getAttribute('value') || label.getAttribute('data-swatch') || swatch.getAttribute('data-tooltip') || '';
      value = value.replace(/^\s+|\s+$/g, '');
      if (value) {
        input.setAttribute('aria-label', 'Color: ' + value);
      }

      label.setAttribute('aria-hidden', 'true');
      label.querySelectorAll('img, svg, [role="img"]').forEach(function (el) {
        if (el.tagName && el.tagName.toLowerCase() === 'img') { el.setAttribute('alt', ''); }
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('role', 'presentation');
      });
    }
  }

  /* Quick-add modal: the slideshow clones its slide cells for the carousel,
     so every image gets announced several times. Mark the whole media group
     decorative - the product title link beside it provides the name. */
  function hideQuickAddSlides(root) {
    if (!root || !root.querySelectorAll) { return; }
    var groups = root.querySelectorAll('.product-quick-add [data-product-single-media-group]');
    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];

      group.querySelectorAll('.product__slide, .product__photo, figure, [data-product-single-media-wrapper]').forEach(function (el) {
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('role', 'presentation');
        if (el.hasAttribute('tabindex')) { el.setAttribute('tabindex', '-1'); }
      });

      group.querySelectorAll('img').forEach(function (img) {
        img.setAttribute('alt', '');
        img.setAttribute('aria-hidden', 'true');
        img.setAttribute('role', 'presentation');
      });
    }

  }

  /* ------------------------------------------------------------------ *
   * 10. Instagram feed: give each post a unique accessible name         *
   * ------------------------------------------------------------------ */

  function initInstagramPostNames() {
    var INSTA_ROOT = '#insta-feed';
    var ITEM = '.instafeed-new-layout-item';
    var BUTTON = '[data-instafeed-role-button="true"], [role="button"][data-instafeed-open-id]';
    var CAPTION_PREFIX = 'Instagram post with the caption: ';

    function captionFor(root) {
      if (!root) { return ''; }
      var img = root.querySelector('img');
      var alt = img && img.getAttribute('alt');
      if (!alt || !alt.trim()) { return ''; }
      // The app puts the caption inside the image alt with a known prefix.
      if (alt.indexOf(CAPTION_PREFIX) === 0) { return alt.slice(CAPTION_PREFIX.length).trim(); }
      if (/instagram|insta/i.test(alt)) { return ''; }
      return alt.trim();
    }

    function namePost(node) {
      if (!node || node.nodeType !== 1) { return; }
      var roots;
      if (node.matches(ITEM)) {
        roots = [node];
      } else {
        roots = node.querySelectorAll ? node.querySelectorAll(ITEM) : [];
      }
      Array.prototype.forEach.call(roots, function (item) {
        var button = item.querySelector(BUTTON);
        if (!button) { return; }
        var caption = captionFor(item);
        if (!caption) { return; }
        button.setAttribute('aria-label', 'Instagram post: ' + caption);
      });
    }

    namePost(document.body);
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) { namePost(node); }
        });
      });
    });
    if (document.querySelector(INSTA_ROOT)) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ------------------------------------------------------------------ *
   * 10. Collection filters: announce AJAX result changes + keep focus   *
   * ------------------------------------------------------------------ */

  function initCollectionFilterAnnouncements() {
    var status = document.querySelector('[data-collection-status]');
    var products = document.querySelector('[data-products-grid]');
    if (!status || !products) { return; }

    var lastFocusedSelector = '';

    function rememberFocus(target) {
      if (!target || !target.matches) { return; }
      if (target.name) {
        lastFocusedSelector = '[name="' + target.name + '"]';
      } else if (target.id) {
        lastFocusedSelector = '#' + target.id;
      }
    }

    document.addEventListener('change', function (e) {
      if (e.target.closest('[data-collection-sidebar]')) { rememberFocus(e.target); }
    }, true);

    document.addEventListener('input', function (e) {
      if (e.target.closest('[data-collection-sidebar]')) { rememberFocus(e.target); }
    }, true);

    document.addEventListener('click', function (e) {
      var control = e.target.closest('[data-collection-sidebar] a, [data-collection-sidebar] button, [data-filter-update-url]');
      if (control) { rememberFocus(control); }
    }, true);

    function announceResults() {
      var count = products.querySelectorAll('[data-product-grid-item], .product-item').length;
      status.textContent = '';
      void status.offsetWidth;
      status.textContent = count + ' products displayed.';
      if (lastFocusedSelector) {
        window.setTimeout(function () {
          var nextFocus = document.querySelector(lastFocusedSelector);
          if (nextFocus) {
            try { nextFocus.focus(); } catch (err) {}
          }
        }, 50);
      }
    }

    var observer = new MutationObserver(function () {
      window.clearTimeout(observer._a11yTimer);
      observer._a11yTimer = window.setTimeout(announceResults, 150);
    });
    observer.observe(products, { childList: true, subtree: true });
  }

  function initPriceRangeKeyboard() {
    function numberValue(input, fallback) {
      if (!input) { return fallback; }
      var value = parseInt(input.value || input.getAttribute('placeholder') || fallback, 10);
      return isNaN(value) ? fallback : value;
    }

    function setInput(input, value) {
      if (!input) { return; }
      input.value = String(value);
    }

    function isRangeKey(key) {
      return ['ArrowLeft', 'ArrowDown', 'ArrowRight', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp'].indexOf(key) !== -1;
    }

    function dispatchRangeUpdate(holder) {
      var slider = holder && holder.querySelector('[data-range-slider]');
      if (!slider) { return; }
      slider.dispatchEvent(new CustomEvent('theme:range:update', { bubbles: true }));
    }

    function updateAria(holder) {
      var minInput = holder.querySelector('[data-field-price-min]');
      var maxInput = holder.querySelector('[data-field-price-max]');
      var left = holder.querySelector('[data-range-left]');
      var right = holder.querySelector('[data-range-right]');
      var slider = holder.querySelector('[data-range-slider]');
      var minLimit = parseInt((slider && slider.getAttribute('data-se-min')) || 0, 10) || 0;
      var maxLimit = parseInt((slider && slider.getAttribute('data-se-max')) || (maxInput && maxInput.getAttribute('max')) || 0, 10) || 0;
      var min = numberValue(minInput, minLimit);
      var max = numberValue(maxInput, maxLimit);
      if (left) { left.setAttribute('aria-valuenow', String(min)); }
      if (right) { right.setAttribute('aria-valuenow', String(max)); }
    }

    function updateVisualRange(holder, min, max) {
      var slider = holder.querySelector('[data-range-slider]');
      var left = holder.querySelector('[data-range-left]');
      var right = holder.querySelector('[data-range-right]');
      var line = holder.querySelector('[data-range-line]');
      if (!slider || !left || !right || !line) { return; }

      var minimum = parseInt(slider.getAttribute('data-se-min') || 0, 10) || 0;
      var maximum = parseInt(slider.getAttribute('data-se-max') || 0, 10) || 0;
      var width = slider.offsetWidth;
      var dotWidth = left.offsetWidth || 36;
      var normalize = 26;
      var usableWidth = width - (dotWidth + normalize);
      if (!width || !usableWidth || maximum <= minimum) { return; }

      var minRatio = (min - minimum) / (maximum - minimum);
      var maxRatio = (max - minimum) / (maximum - minimum);
      left.style.left = Math.ceil(minRatio * usableWidth) + 'px';
      right.style.left = Math.ceil(maxRatio * usableWidth + normalize) + 'px';
      line.style.marginLeft = left.offsetLeft + 'px';
      line.style.width = (right.offsetLeft - left.offsetLeft) + 'px';
      slider.setAttribute('data-se-min-value', String(min));
      slider.setAttribute('data-se-max-value', String(max));
    }

    document.querySelectorAll('[data-range-holder]').forEach(function (holder) {
      if (holder._a11yPriceRangeBound) { return; }
      holder._a11yPriceRangeBound = true;
      var minInput = holder.querySelector('[data-field-price-min]');
      var maxInput = holder.querySelector('[data-field-price-max]');
      var left = holder.querySelector('[data-range-left]');
      var right = holder.querySelector('[data-range-right]');
      var slider = holder.querySelector('[data-range-slider]');
      var minimum = parseInt((slider && slider.getAttribute('data-se-min')) || 0, 10) || 0;
      var maximum = parseInt((slider && slider.getAttribute('data-se-max')) || (maxInput && maxInput.getAttribute('max')) || 0, 10) || 0;

      function handleKey(input, otherInput, isMin, event) {
        var key = event.key;
        if (!isRangeKey(key)) { return; }
        event.preventDefault();
        var value = numberValue(input, isMin ? minimum : maximum);
        var other = numberValue(otherInput, isMin ? maximum : minimum);
        var step = (key === 'PageDown' || key === 'PageUp') ? 10 : 1;
        if (key === 'ArrowLeft' || key === 'ArrowDown' || key === 'PageDown') { value -= step; }
        if (key === 'ArrowRight' || key === 'ArrowUp' || key === 'PageUp') { value += step; }
        if (key === 'Home') { value = isMin ? minimum : other; }
        if (key === 'End') { value = isMin ? other : maximum; }
        value = Math.max(minimum, Math.min(maximum, value));
        if (isMin) { value = Math.min(value, other); }
        else { value = Math.max(value, other); }
        setInput(input, value);
        updateVisualRange(holder, isMin ? value : other, isMin ? other : value);
        updateAria(holder);
        holder._a11yRangePendingUpdate = true;
      }

      function handleKeyup(event) {
        if (!isRangeKey(event.key) || !holder._a11yRangePendingUpdate) { return; }
        holder._a11yRangePendingUpdate = false;
        dispatchRangeUpdate(holder);
      }

      if (left && minInput) {
        left.addEventListener('keydown', function (event) { handleKey(minInput, maxInput, true, event); });
        left.addEventListener('keyup', handleKeyup);
      }
      if (right && maxInput) {
        right.addEventListener('keydown', function (event) { handleKey(maxInput, minInput, false, event); });
        right.addEventListener('keyup', handleKeyup);
      }
      ['input', 'change'].forEach(function (eventName) {
        if (minInput) { minInput.addEventListener(eventName, function () { updateAria(holder); }); }
        if (maxInput) { maxInput.addEventListener(eventName, function () { updateAria(holder); }); }
      });
      updateAria(holder);
    });

    if (!document.body._a11yPriceRangeObserver) {
      document.body._a11yPriceRangeObserver = new MutationObserver(function (mutations) {
        var hasRange = mutations.some(function (mutation) {
          return Array.prototype.slice.call(mutation.addedNodes).some(function (node) {
            return node.nodeType === 1 && (node.matches('[data-range-holder]') || node.querySelector('[data-range-holder]'));
          });
        });
        if (hasRange) { initPriceRangeKeyboard(); }
      });
      document.body._a11yPriceRangeObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ------------------------------------------------------------------ *
   * 11. Popups: focus trap and return focus on close                    *
   * ------------------------------------------------------------------ */

  function initPopupFocusManagement() {
    var popupSelectors = '[data-large-promo] [role="dialog"], [data-newsletter] [role="dialog"], #popup-account';
    var lastTrigger = null;

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('#accountDropdown, [data-popup-open], [data-newsletter-open]');
      if (trigger) {
        lastTrigger = trigger;
        if (trigger.id === 'accountDropdown') { trigger.setAttribute('aria-expanded', 'true'); }
      }
      if (e.target.closest('#closeBtn, #account-backdrop')) {
        var accountTrigger = document.getElementById('accountDropdown');
        if (accountTrigger) { accountTrigger.setAttribute('aria-expanded', 'false'); }
      }
    }, true);

    function visibleDialog() {
      return Array.prototype.slice.call(document.querySelectorAll(popupSelectors)).find(function (dialog) {
        var style = window.getComputedStyle(dialog);
        return style.display !== 'none' && style.visibility !== 'hidden' && dialog.offsetParent !== null;
      });
    }

    document.addEventListener('keydown', function (e) {
      var dialog = visibleDialog();
      if (!dialog) { return; }
      if (e.key === 'Escape') {
        var close = dialog.querySelector('[data-close-modal], [data-newsletter-close], #closeBtn, .close');
        if (close) { close.click(); }
        var accountTrigger = document.getElementById('accountDropdown');
        if (accountTrigger) { accountTrigger.setAttribute('aria-expanded', 'false'); }
        if (lastTrigger) {
          window.setTimeout(function () { try { lastTrigger.focus(); } catch (err) {} }, 0);
        }
        return;
      }
      if (e.key !== 'Tab') { return; }
      var focusables = Array.prototype.slice.call(dialog.querySelectorAll(FOCUSABLE)).filter(isFocusable);
      if (!focusables.length) { return; }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    var observer = new MutationObserver(function () {
      var dialog = visibleDialog();
      if (!dialog || dialog.contains(document.activeElement)) { return; }
      var target = dialog.querySelector(FOCUSABLE) || dialog;
      if (!target.hasAttribute('tabindex') && target === dialog) { target.setAttribute('tabindex', '-1'); }
      try { target.focus(); } catch (err) {}
    });
    observer.observe(document.body, { attributes: true, childList: true, subtree: true, attributeFilter: ['class', 'style', 'open', 'aria-hidden'] });
  }

  function initDecorativeKlaviyoImageAlt() {
    function update(root) {
      var images = [];
      if (root && root.nodeType === 1 && root.matches('.klaviyo-form img[alt="Black Friday"], .needsclick img[alt="Black Friday"]')) {
        images.push(root);
      }
      (root || document).querySelectorAll('.klaviyo-form img[alt="Black Friday"], .needsclick img[alt="Black Friday"]').forEach(function (image) {
        images.push(image);
      });
      images.forEach(function (image) {
        image.setAttribute('alt', '');
      });
    }

    update(document);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') {
          update(mutation.target);
          return;
        }
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) { update(node); }
        });
      });
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['alt', 'class'] });
  }

  function initKlaviyoFieldErrors() {
    var count = 0;
    var busy = false;
    var fieldWrapSelector = '.klaviyo-textinput, .klaviyo-emailinput, .klaviyo-select';
    var formSelector = '.klaviyo-form, form[aria-live], [data-testid="form-component"]';

    function cleanLabel(text) {
      return (text || '').replace(/\s*\*$/, '').replace(/\s+/g, ' ').trim();
    }

    function labelFor(input) {
      var label = input.id && document.querySelector('label[for="' + input.id + '"]');
      return cleanLabel((label && label.textContent) || input.getAttribute('aria-label') || input.placeholder) || 'This field';
    }

    function ensureLabel(wrapper, input) {
      var label = labelFor(input);
      if (!input.id) { input.id = 'kl-field-' + (++count); }
      if (!wrapper.querySelector('label[for="' + input.id + '"]')) {
        var labelNode = document.createElement('label');
        labelNode.className = 'kl-visible-label';
        labelNode.setAttribute('for', input.id);
        labelNode.textContent = label;
        wrapper.insertBefore(labelNode, wrapper.firstChild);
      }
    }

    function errorFor(wrapper, input) {
      var error = null;
      wrapper.querySelectorAll('*').forEach(function (node) {
        if (error || node === input || node.tagName === 'LABEL' || node.children.length) { return; }
        if (/required|valid|invalid|enter|field/i.test(node.textContent || '')) { error = node; }
      });
      return error;
    }

    function candidateErrors(form) {
      var errors = [];
      (form || document).querySelectorAll('[id], [role="alert"], span, div, p').forEach(function (node) {
        var text = (node.textContent || "").trim();
        if (!text || node.children.length > 1) return;
        if (node.querySelector('input, select, textarea, button, label')) return;
        if (/required|valid|invalid|please enter|field/i.test(text)) errors.push(node);
      });
      return errors;
    }

    function cleanupLiveRegions(form) {
      if (!form || form.nodeType !== 1) { return; }
      form.removeAttribute('aria-live');
      form.removeAttribute('role');
      form.querySelectorAll('[role="alert"], [aria-live]').forEach(function (node) {
        node.removeAttribute('role');
        node.removeAttribute('aria-live');
      });
    }

    function errorForInput(input, wrapper, form, index) {
      var local = wrapper && errorFor(wrapper, input);
      if (local) { return local; }

      var label = labelFor(input).toLowerCase();
      var errors = candidateErrors(form);
      var matched = errors.find(function (error) {
        return (error.textContent || '').trim().toLowerCase().indexOf(label) === 0;
      });
      if (matched) { return matched; }

      return errors[index] || null;
    }

    function normalizeError(label, msg) {
      if (/email/i.test(label) && /valid|invalid|format/i.test(msg)) {
        return label + ': Please enter a valid email address';
      }
      return label + ': This field is required';
    }

    function ensureAlertSymbol(error) {
      if (error.querySelector('[data-kl-error-symbol]')) { return; }
      var symbol = document.createElement('span');
      symbol.setAttribute('aria-hidden', 'true');
      symbol.setAttribute('data-kl-error-symbol', '');
      symbol.className = 'kl-error-symbol';
      symbol.textContent = '⚠';
      error.insertBefore(symbol, error.firstChild);
    }

    function wireHeading(root) {
      (root || document).querySelectorAll('.klaviyo-form, .needsclick').forEach(function (dialog) {
        var heading = null;
        dialog.querySelectorAll('h1, h2, h3, h4, h5, h6, p, div, span').forEach(function (node) {
          if (heading || node.children.length > 1) { return; }
          if (/^you\s+must\s+be\s+new\s+here\.?$/i.test((node.textContent || '').trim())) { heading = node; }
        });
        if (!heading) { return; }
        if (!heading.id) { heading.id = 'kl-promo-heading-' + (++count); }
        if (!/^H[1-6]$/.test(heading.tagName)) {
          heading.setAttribute('role', 'heading');
          heading.setAttribute('aria-level', '2');
        }
        var container = dialog.closest('[role="dialog"]') || dialog;
        container.setAttribute('aria-labelledby', heading.id);
      });
    }

    function update(root) {
      if (busy) { return; }
      busy = true;
      var forms = [];
      if (root && root.nodeType === 1 && root.matches(formSelector)) { forms.push(root); }
      (root || document).querySelectorAll(formSelector).forEach(function (form) { forms.push(form); });
      if (!forms.length) {
        busy = false;
        return;
      }

      forms.forEach(function (form) {
        cleanupLiveRegions(form);
        var fields = Array.prototype.slice.call(form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]), select, textarea'));
        fields.forEach(function (input, index) {
        var wrapper = input.closest(fieldWrapSelector) || input.parentElement || form;
        if (!input) { return; }
        ensureLabel(wrapper, input);
        var error = errorForInput(input, wrapper, form, index);
        if (error && (error.contains(input) || error.querySelector('input, select, textarea, button'))) error = null;
        if (!error) {
          input.removeAttribute('aria-describedby');
          if (input.getAttribute('aria-invalid') === 'true') { input.setAttribute('aria-invalid', 'false'); }
          return;
        }

        if (!error.id) { error.id = 'kl-field-error-' + (++count); }
        var label = labelFor(input);
        var message = error.textContent.trim();
        error.textContent = normalizeError(label, message);
        ensureAlertSymbol(error);
        error.removeAttribute('role');
        error.removeAttribute('aria-live');
        input.setAttribute('aria-describedby', error.id);
        input.setAttribute('aria-invalid', 'true');
      });
      });
      wireHeading(root || document);
      busy = false;
    }

    update(document);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'characterData' || mutation.type === 'attributes') {
          update(mutation.target.parentElement || document);
          return;
        }
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) { update(node); }
        });
      });
    }).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'aria-invalid', 'aria-describedby'] });

    document.addEventListener('click', function (event) {
      if (!event.target.closest('.klaviyo-form-button, button[type="submit"]')) { return; }
      window.setTimeout(function () {
        update(document);
        var firstInvalid = document.querySelector('.klaviyo-form [aria-invalid="true"], [data-testid="form-component"] [aria-invalid="true"]');
        if (firstInvalid) {
          try { firstInvalid.focus({ preventScroll: true }); } catch (err) { firstInvalid.focus(); }
        }
      }, 250);
    });
  }

  /* ------------------------------------------------------------------ *
   * 12. Sliders: label generated previous/next buttons                  *
   * ------------------------------------------------------------------ */

  function initSliderButtonLabels() {
    function activateButton(button) {
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') { return; }
      button.click();
    }

    function prepareButton(button, label) {
      button.setAttribute('aria-label', label);
      if (button.tagName !== 'BUTTON') {
        button.setAttribute('role', 'button');
        if (!button.hasAttribute('tabindex')) { button.setAttribute('tabindex', '0'); }
      }
      if (button._a11ySliderButtonBound) { return; }
      button._a11ySliderButtonBound = true;
      button.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') { return; }
        event.preventDefault();
        event.stopPropagation();
        activateButton(button);
      });
    }

    function labelButtons(root) {
      (root || document).querySelectorAll('.flickity-button.previous, .flickity-prev-next-button.previous').forEach(function (button) {
        prepareButton(button, 'Previous slide');
      });
      (root || document).querySelectorAll('.flickity-button.next, .flickity-prev-next-button.next').forEach(function (button) {
        prepareButton(button, 'Next slide');
      });
    }
    labelButtons(document);
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) { labelButtons(node); }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initSliderRepositionGuard() {
    if (!window.Flickity || !window.Flickity.data) { return; }

    function reposition() {
      document.querySelectorAll('.flickity-enabled, [data-slider], [data-slider-logos]').forEach(function (slider) {
        var instance = window.Flickity.data(slider);
        if (!instance) { return; }
        try {
          if (instance.resize) { instance.resize(); }
          if (instance.reposition) { instance.reposition(); }
        } catch (err) {}
      });
    }

    window.addEventListener('load', function () { window.setTimeout(reposition, 100); });
    document.addEventListener('lazyloaded', reposition);
  }

  function initSlideshowPauseControls() {
    document.querySelectorAll('[data-slideshow-pause], [data-motion-pause]').forEach(function (button) {
      if (button._a11yPauseBound) { return; }
      button._a11yPauseBound = true;

      var target = document.getElementById(button.getAttribute('aria-controls'));
      var targets = target ? [target] : [];
      if (button.classList.contains('logos__pause')) {
        var logosSection = button.closest('.logos');
        var textSlider = logosSection && logosSection.querySelector('[data-slider-text]');
        if (textSlider && targets.indexOf(textSlider) === -1) { targets.push(textSlider); }
      }
      var text = button.querySelector('[data-slideshow-pause-text]') || button;
      var pauseLabel = button.getAttribute('data-pause-label') || 'Pause slideshow';
      var playLabel = button.getAttribute('data-play-label') || 'Play slideshow';

      function flickity() {
        if (!targets.length) { return null; }
        return flickityInstances()[0] || null;
      }

      function flickityInstances() {
        if (!targets.length) { return []; }
        var nodes = [];
        targets.forEach(function (controlledTarget) {
          nodes = nodes.concat([controlledTarget], Array.prototype.slice.call(controlledTarget.querySelectorAll('.flickity-enabled, [data-slider], [data-slider-logos], [data-slider-text]')));
        });
        var instances = [];
        var jq = window.jQuery || window.$;

        function addInstance(instance) {
          if (instance && instances.indexOf(instance) === -1) { instances.push(instance); }
        }

        nodes.forEach(function (node) {
          var instance = null;
          if (window.Flickity && window.Flickity.data) {
            instance = window.Flickity.data(node);
          }
          addInstance(instance);
          if (jq && jq.fn) {
            try {
              addInstance(jq(node).data('flickity'));
              addInstance(jq.data(node, 'flickity'));
            } catch (err) {}
          }
        });
        return instances;
      }

      function storedAutoplay(instance) {
        if (instance && instance._a11yOriginalAutoplay) { return instance._a11yOriginalAutoplay; }
        var rawOptions = target && target.getAttribute('data-options');
        if (rawOptions) {
          try {
            var options = JSON.parse(rawOptions);
            if (options.autoPlay) { return options.autoPlay; }
          } catch (err) {}
        }
        return 4000;
      }

      function setFlickityPaused(paused) {
        flickityInstances().forEach(function (instance) {
          if (paused) {
            if (!instance._a11yOriginalAutoplay) {
              instance._a11yOriginalAutoplay = instance.options && instance.options.autoPlay ? instance.options.autoPlay : storedAutoplay(instance);
            }
            if (instance.options) { instance.options.autoPlay = false; }
            if (instance.stopPlayer) { instance.stopPlayer(); }
            if (instance.player) {
              if (instance.player.stop) { instance.player.stop(); }
              if (instance.player.clear) { instance.player.clear(); }
              if (instance.player.timeout) { window.clearTimeout(instance.player.timeout); }
              instance.player.isPlaying = false;
            }
          } else {
            if (instance.options) { instance.options.autoPlay = storedAutoplay(instance); }
            if (instance.playPlayer) { instance.playPlayer(); }
            if (instance.player) {
              instance.player.isPaused = false;
              instance.player.isPlaying = false;
              if (instance.player.play) { instance.player.play(); }
            }
          }
        });
      }

      function setPaused(paused) {
        button.setAttribute('aria-pressed', paused ? 'true' : 'false');
        button.setAttribute('aria-label', paused ? playLabel : pauseLabel);
        button.classList.toggle('is-paused', paused);
        targets.forEach(function (controlledTarget) {
          controlledTarget.classList.toggle('is-motion-paused', paused);
          if (paused) {
            controlledTarget.setAttribute('data-user-paused', 'true');
          } else {
            controlledTarget.removeAttribute('data-user-paused');
          }
          controlledTarget.querySelectorAll('.ticker--animated, [data-ticker-text]').forEach(function (el) {
            el.style.animationPlayState = paused ? 'paused' : '';
          });
        });
        freezeVisibleMotion(paused);
        lockLogosSelectedState(paused);
        text.textContent = paused ? playLabel : pauseLabel;
      }

      function lockLogosSelectedState(paused) {
        if (!button.classList.contains('logos__pause') || !target) { return; }

        if (!paused) {
          if (button._a11yLogosObserver) {
            button._a11yLogosObserver.disconnect();
            button._a11yLogosObserver = null;
          }
          button._a11yLogosSnapshot = null;
          return;
        }

        var slides = Array.prototype.slice.call(target.querySelectorAll('[data-slide-index]'));
        button._a11yLogosSnapshot = slides.map(function (slide) {
          return { slide: slide, className: slide.className };
        });

        function restoreSlides() {
          if (!button._a11yLogosSnapshot) { return; }
          button._a11yRestoringLogos = true;
          button._a11yLogosSnapshot.forEach(function (entry) {
            if (entry.slide.className !== entry.className) {
              entry.slide.className = entry.className;
            }
          });
          button._a11yRestoringLogos = false;
        }

        restoreSlides();
        if (!button._a11yLogosObserver) {
          button._a11yLogosObserver = new MutationObserver(function () {
            if (!button._a11yRestoringLogos && button.getAttribute('aria-pressed') === 'true') {
              restoreSlides();
            }
          });
          button._a11yLogosObserver.observe(target, { attributes: true, attributeFilter: ['class'], subtree: true });
        }
      }

      function freezeVisibleMotion(paused) {
        if (!targets.length) { return; }

        function motionElements() {
          var elements = [];
          targets.forEach(function (controlledTarget) {
            elements = elements.concat(Array.prototype.slice.call(controlledTarget.querySelectorAll('[data-ticker-scale], .ticker--animated, [data-ticker-text]')));
          });
          return elements;
        }

        function lockMotion() {
          motionElements().forEach(function (el) {
            if (!el.hasAttribute('data-a11y-transform')) {
              el.setAttribute('data-a11y-transform', el.style.getPropertyValue('transform') || '');
              var computedTransform = window.getComputedStyle(el).transform;
              el.setAttribute('data-a11y-freeze-transform', computedTransform && computedTransform !== 'none' ? computedTransform : '');
            }
            var frozenTransform = el.getAttribute('data-a11y-freeze-transform');
            if (frozenTransform) {
              el.style.setProperty('transform', frozenTransform, 'important');
            }
            el.style.setProperty('animation-play-state', 'paused', 'important');
            el.style.setProperty('transition', 'none', 'important');
          });
        }

        motionElements().forEach(function (el) {
          if (paused) {
            lockMotion();
            if (!button._a11yFreezeTimer) {
              button._a11yFreezeTimer = window.setInterval(lockMotion, 100);
            }
          } else {
            if (button._a11yFreezeTimer) {
              window.clearInterval(button._a11yFreezeTimer);
              button._a11yFreezeTimer = null;
            }
            if (el.hasAttribute('data-a11y-transform')) {
              var previousTransform = el.getAttribute('data-a11y-transform');
              if (previousTransform) {
                el.style.setProperty('transform', previousTransform);
              } else {
                el.style.removeProperty('transform');
              }
              el.removeAttribute('data-a11y-transform');
              el.removeAttribute('data-a11y-freeze-transform');
            }
            el.style.removeProperty('animation-play-state');
            el.style.removeProperty('transition');
          }
        });
      }

      function dispatchHover(type) {
        if (!targets.length) { return; }
        targets.forEach(function (controlledTarget) {
          try {
            controlledTarget.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window }));
          } catch (err) {}
        });
      }

      function isUserPaused() {
        return button.getAttribute('aria-pressed') === 'true' || button.classList.contains('is-paused');
      }

      function reassertUserPause() {
        if (!isUserPaused()) { return; }
        window.clearTimeout(button._a11yReassertTimer);
        button._a11yReassertTimer = window.setTimeout(function () {
          if (!isUserPaused()) { return; }
          setFlickityPaused(true);
          dispatchHover('mouseenter');
          setPaused(true);
        }, 80);
      }

      targets.forEach(function (controlledTarget) {
        ['dragend', 'pointerup', 'touchend', 'mouseup', 'mouseleave'].forEach(function (eventName) {
          controlledTarget.addEventListener(eventName, reassertUserPause, true);
        });
      });

      if ('IntersectionObserver' in window && target) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) { reassertUserPause(); }
          });
        }, { threshold: 0.01 }).observe(target);
      }

      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) { reassertUserPause(); }
      });

      document.addEventListener('shopify:section:load', reassertUserPause);

      button.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var isPaused = button.getAttribute('aria-pressed') === 'true';
        var instance = flickity();

        setPaused(!isPaused);
        setFlickityPaused(!isPaused);
        if (instance) {
          if (isPaused) {
            if (instance.playPlayer) { instance.playPlayer(); }
            dispatchHover('mouseleave');
          } else {
            if (instance.stopPlayer) { instance.stopPlayer(); }
            dispatchHover('mouseenter');
          }
        } else if (isPaused) {
          dispatchHover('mouseleave');
        } else {
          dispatchHover('mouseenter');
        }
      }, true);
    });
  }

  /* ------------------------------------------------------------------ *
   * 13. aria-hidden containers must not contain focusable elements      *
   * ------------------------------------------------------------------ */

  function initAriaHiddenFocusGuard() {
    function isHiddenContainerActive(hidden) {
      return hidden.getAttribute('aria-hidden') === 'true' && !hidden.classList.contains('is-visible');
    }

    function guard(root) {
      (root || document).querySelectorAll('[aria-hidden="true"]').forEach(function (hidden) {
        if (!isHiddenContainerActive(hidden)) { return; }
        // Any interactive element inside an aria-hidden container must not
        // be focusable, otherwise keyboard users can tab into content that
        // is hidden from screen readers (e.g. cloned partner-logo slides in
        // the Flickity slider).
        hidden.querySelectorAll('button, [href], input, select, textarea, [tabindex]').forEach(function (el) {
          if (el.tabIndex >= 0 || el.hasAttribute('tabindex')) {
            el.setAttribute('tabindex', '-1');
            el.setAttribute('data-a11y-restored-tabindex', '');
          }
        });
      });
      // Restore focusability when a container becomes visible again.
      (root || document).querySelectorAll('[data-a11y-restored-tabindex]').forEach(function (el) {
        var hiddenParent = el.closest('[aria-hidden="true"]');
        if (hiddenParent && isHiddenContainerActive(hiddenParent)) { return; }
        el.removeAttribute('tabindex');
        el.removeAttribute('data-a11y-restored-tabindex');
      });
    }

    guard(document);
    if (window.Flickity) {
      document.addEventListener('pluginWhyCloneSet', function () { guard(document); });
    }
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.target.nodeType === 1) {
          guard(mutation.target);
        }
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) { guard(node); }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'class'] });
  }

  function initPlainTextContactLinks() {
    if (!/\/pages\/(customer-care|terms-conditions)\/?$/.test(window.location.pathname)) { return; }
    var root = document.getElementById('MainContent');
    if (!root) { return; }

    var matcher = /(info@stormcreek\.com)|(\(?651\)?[-\s]480[-\s]3000)|((?:www\.)?stormcreek\.com)/gi;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        var parent = node.parentElement;
        if (!parent || parent.closest('a, script, style, noscript, textarea, select, input, button')) {
          return NodeFilter.FILTER_REJECT;
        }
        matcher.lastIndex = 0;
        return matcher.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });

    var nodes = [];
    while (walker.nextNode()) { nodes.push(walker.currentNode); }

    nodes.forEach(function (node) {
      matcher.lastIndex = 0;
      var text = node.nodeValue;
      var fragment = document.createDocumentFragment();
      var lastIndex = 0;
      var match;

      while ((match = matcher.exec(text)) !== null) {
        if (match.index > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
        }

        var value = match[0];
        var link = document.createElement('a');
        link.className = 'a11y-inline-link';
        link.textContent = value;

        if (match[1]) {
          link.href = 'mailto:info@stormcreek.com';
          link.setAttribute('aria-label', 'Email Storm Creek at info@stormcreek.com');
        } else if (match[2]) {
          link.href = 'tel:651-480-3000';
          link.setAttribute('aria-label', 'Call Storm Creek at 651-480-3000');
        } else {
          link.href = 'https://www.stormcreek.com/';
          link.setAttribute('aria-label', 'Visit stormcreek.com');
        }

        fragment.appendChild(link);
        lastIndex = matcher.lastIndex;
      }

      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }

      node.parentNode.replaceChild(fragment, node);
    });
  }

  function initReturnPortalLinkNames() {
    document.querySelectorAll('a[href*="returns.stormcreek.com"]').forEach(function (link) {
      link.setAttribute('aria-label', 'Start a return in the Storm Creek returns portal');
      link.removeAttribute('title');
    });

    document.querySelectorAll('a[href*="/pages/front-runner-rewards"]').forEach(function (link) {
      link.setAttribute('aria-label', 'Storm Creek Front Runner Rewards Page');
    });
  }

  function initPrivacyPolicyHeadingHierarchy() {
    if (window.location.pathname.replace(/\/$/, '') !== '/pages/privacy-policy') { return; }

    var main = document.querySelector('#MainContent, main, [role="main"]') || document.body;
    var headings = Array.prototype.slice.call(main.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    if (!headings.length) { return; }

    function headingLevel(heading) {
      return parseInt(heading.tagName.slice(1), 10);
    }

    function changeHeadingLevel(heading, level) {
      if (!heading || headingLevel(heading) === level) { return heading; }
      var replacement = document.createElement('h' + level);
      Array.prototype.slice.call(heading.attributes).forEach(function (attr) {
        replacement.setAttribute(attr.name, attr.value);
      });
      replacement.innerHTML = heading.innerHTML;
      heading.parentNode.replaceChild(replacement, heading);
      return replacement;
    }

    var firstH1 = headings.filter(function (heading) { return headingLevel(heading) === 1; })[0];
    if (!firstH1) {
      headings[0] = changeHeadingLevel(headings[0], 1);
    }

    headings = Array.prototype.slice.call(main.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    var seenH1 = false;
    var previousLevel = 0;
    headings.forEach(function (heading) {
      var level = headingLevel(heading);
      if (level === 1) {
        if (!seenH1) {
          seenH1 = true;
          previousLevel = 1;
          return;
        }
        heading = changeHeadingLevel(heading, 2);
        level = 2;
      }
      if (previousLevel && level > previousLevel + 1) {
        heading = changeHeadingLevel(heading, previousLevel + 1);
        level = previousLevel + 1;
      }
      previousLevel = level;
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot                                                                *
   * ------------------------------------------------------------------ */

  function boot() {
    window.__a11ySearchStatus = document.createElement('div');
    window.__a11ySearchStatus.setAttribute('role', 'status');
    window.__a11ySearchStatus.setAttribute('aria-live', 'polite');
    window.__a11ySearchStatus.className = 'visually-hidden';
    document.body.appendChild(window.__a11ySearchStatus);

    initHeaderNav();
    initDropdownToggles();
    initCartDrawer();
    initMobileMenu();
    initQuickAddDialogs();
    initCartAnnouncements();
    initSearchFocus();
    initAriaCleanup();
    initGridSwatchObserver();
    initCollectionFilterAnnouncements();
    initPriceRangeKeyboard();
    initPopupFocusManagement();
    initDecorativeKlaviyoImageAlt();
    initKlaviyoFieldErrors();
    initSlideshowPauseControls();
    initSliderButtonLabels();
    initSliderRepositionGuard();
    initAriaHiddenFocusGuard();
    initPlainTextContactLinks();
    initReturnPortalLinkNames();
    initPrivacyPolicyHeadingHierarchy();
    initInstagramPostNames();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
